import React, { memo, FC } from "react"
import { SadIcon, ShockIcon, SmileyIcon } from "../../assets/icons/smily/Smily"
import { blue, green, orange, red } from "@ant-design/colors"
import { Space } from "antd"
import { FireOutlined } from "@ant-design/icons"
import { MemberIconSvg, PrerequisiteCoursesIconSvg } from "../../assets/icons/OtherIcons"
import { useMediaSize } from "../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import { AppSettings } from "../../layouts/Main/MainLayout"

type CourseTableLegendProps = {
    showStatus?: boolean
    showRestrictions?: boolean
    showMemberDiscount?: boolean
    appSettings: AppSettings
    style?: React.CSSProperties
}

const CourseTableLegend: FC<CourseTableLegendProps> = ({
    showStatus = true,
    showRestrictions = false,
    showMemberDiscount = false,
    appSettings,
    style,
}) => {
    const mediaSize = useMediaSize()
    const iconSize = 16
    const Status = ({ status }: { status: "free" | "shock" | "sad" }) => {
        const description = {
            free: "Viele Plätze",
            shock: "Wenige Plätze",
            sad: "Ausgebucht",
        }

        const color = {
            free: green[1],
            shock: orange[1],
            sad: red[1],
        }

        const entryStyle: React.CSSProperties = {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            backgroundColor: color[status],
            opacity: 0.8,
            padding: "3px 10px 0px 3px",
            height: iconSize + 6,
            borderRadius: iconSize - 4,
        }

        const descriptionStyle: React.CSSProperties = {
            height: "100%",
            display: "flex",
            alignItems: "center",
            // paddingTop: 1,
            paddingBottom: iconSize / 6,
            marginLeft: iconSize / 6,
        }

        if (status === "free") {
            return (
                <div style={entryStyle}>
                    <SmileyIcon color={green[5]} size={iconSize} />
                    <div style={descriptionStyle}>{description.free}</div>
                </div>
            )
        } else if (status === "shock") {
            return (
                <div style={entryStyle}>
                    <ShockIcon color={orange[5]} size={iconSize} />
                    <div style={descriptionStyle}>{description.shock}</div>
                </div>
            )
        }

        return (
            <div style={entryStyle}>
                <SadIcon color={red[5]} size={iconSize} />
                <div style={descriptionStyle}>{description.sad}</div>
            </div>
        )
    }

    const MemberDiscount = () => {
        const memberDiscountText = appSettings.textEntries?.memberDiscountTitle ?? "Mitgliederpreis"

        const entryStyle: React.CSSProperties = {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            backgroundColor: blue[1],
            opacity: 0.8,
            padding: "3px 10px 3px 3px",
            height: iconSize + 6,
            borderRadius: iconSize - 4,
        }

        if (!showMemberDiscount) {
            return null
        }

        return (
            <Space size={3} style={{ ...entryStyle, color: blue[8], fontWeight: 500 }}>
                <FireOutlined />
                <span>{memberDiscountText}</span>
            </Space>
        )
    }

    const MembersOnly = () => {
        const entryStyle: React.CSSProperties = {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            backgroundColor: green[1],
            opacity: 0.8,
            padding: "3px 10px 3px 5px",
            height: iconSize + 6,
            borderRadius: iconSize - 4,
            // marginTop: -10,
        }

        return (
            <Space size={3} style={{ ...entryStyle, color: green[8], fontWeight: 500 }}>
                <MemberIconSvg color={green[6]} size={23} style={{ paddingBottom: 2 }} />
                <span>{"Nur für Mitglieder"}</span>
            </Space>
        )
    }

    const PrerequisiteCourses = () => {
        const entryStyle: React.CSSProperties = {
            display: "flex",
            alignContent: "center",
            justifyContent: "center",
            backgroundColor: orange[1],
            opacity: 0.8,
            padding: "3px 10px 3px 7px",
            height: iconSize + 6,
            borderRadius: iconSize - 4,
            // marginTop: -3,
        }

        return (
            <Space size={3} style={{ ...entryStyle, color: orange[8], fontWeight: 500 }}>
                <PrerequisiteCoursesIconSvg color={orange[6]} size={20} style={{ marginTop: 7 }} />
                <span>{"Voraussetzungskurse"}</span>
            </Space>
        )
    }

    if (mediaSize.media === "xxl" || mediaSize.media === "xl" || mediaSize.media === "lg") {
        return (
            <Space size={iconSize / 3}>
                {showStatus && (
                    <>
                        <Status status='free' />
                        <Status status='shock' />
                        <Status status='sad' />
                    </>
                )}
                <MemberDiscount />
                {showRestrictions && (
                    <>
                        <MembersOnly />
                        <PrerequisiteCourses />
                    </>
                )}
            </Space>
        )
    }

    return (
        <Space size={iconSize / 3} direction='vertical' style={style}>
            <Space
                size={iconSize / 3}
                style={{
                    width: "100vw",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    flexWrap: "wrap" as const,
                }}
            >
                {showStatus && (
                    <>
                        <Status status='free' />
                        <Status status='shock' />
                        <Status status='sad' />
                    </>
                )}
                <MemberDiscount />
            </Space>
            {showRestrictions && (
                <Space
                    size={iconSize / 3}
                    style={{ width: "100vw", justifyContent: "center", flexWrap: "wrap" as const }}
                >
                    <MembersOnly />
                    <PrerequisiteCourses />
                </Space>
            )}
        </Space>
    )
}

export default memo(CourseTableLegend)
